import { useQuery } from '@apollo/client';
import { type ChangeEvent, useEffect, useMemo, useState } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';
import {
    GetMeasuringPointsDocument,
    ReportBuilderLastGraphStateDocument,
    ReportTemplateEntityType,
} from '@/graphql/types';
import { getPreviewMeasuringPoints, getPreviewProjects } from './previewContextModels';
import {
    createUnsupportedPreviewSelectionEntityTypeError,
    readPreviewRange,
    readPreviewSelection,
    requireSavedGraphState,
    resolvePreviewDefaultRange,
    resolvePreviewDefaultSelection,
    writePreviewRange,
    writePreviewSelection,
} from './previewDefaults';
import { resolvePreviewContextFromQueryData } from './previewContextFactory';
import { formatSwarmType } from './swarmCompatibility';
import type { GraphTimeWindow } from './elements/types';
import type { PreviewSelectorConfig } from './utils';

type PreviewOption = PreviewSelectorConfig['options'][number];

export function usePreviewContextSelection(
    searchParams: URLSearchParams,
    setSearchParams: SetURLSearchParams,
    entityType: ReportTemplateEntityType | null
) {
    const {
        data: previewContextData,
        loading: contextLoading,
        error: contextError,
    } = useQuery(GetMeasuringPointsDocument);
    const {
        data: graphStateData,
        loading: graphStateLoading,
        error: graphStateError,
    } = useQuery(ReportBuilderLastGraphStateDocument, { fetchPolicy: 'network-only' });
    const [initializedAt] = useState(Date.now);
    const previewContextLoading = contextLoading || graphStateLoading;
    const previewContextError = contextError ?? graphStateError;

    const previewProjects = useMemo(
        () => (previewContextData ? getPreviewProjects(previewContextData) : []),
        [previewContextData]
    );
    const previewMeasuringPoints = useMemo(
        () => (previewContextData ? getPreviewMeasuringPoints(previewContextData) : []),
        [previewContextData]
    );
    const previewProjectOptions = useMemo<PreviewOption[]>(
        () => previewProjects.map((project) => [project.id, project.name]),
        [previewProjects]
    );
    const previewMeasuringPointOptions = useMemo<PreviewOption[]>(
        () =>
            previewMeasuringPoints.map((measuringPoint) => {
                const name = measuringPoint.deviceName
                    ? `${measuringPoint.name} (${measuringPoint.deviceName})`
                    : measuringPoint.name;
                return [
                    measuringPoint.id,
                    `${name} · ${formatSwarmType(measuringPoint.swarmType)}`,
                ];
            }),
        [previewMeasuringPoints]
    );
    const previewOptions = useMemo(
        () =>
            entityType && previewContextData
                ? { entityType, projects: previewProjects, measuringPoints: previewMeasuringPoints }
                : null,
        [entityType, previewContextData, previewProjects, previewMeasuringPoints]
    );
    // Validated once loading finishes, whether or not the URL needs defaults.
    const savedGraphState = useMemo(
        () =>
            graphStateLoading || graphStateError
                ? undefined
                : requireSavedGraphState(graphStateData),
        [graphStateLoading, graphStateError, graphStateData]
    );
    const previewContextReady =
        previewOptions !== null &&
        savedGraphState !== undefined &&
        !previewContextLoading &&
        !previewContextError;

    // The URL is the single source of the current selection: unknown IDs and
    // invalid ranges read as missing and are replaced by the defaults below.
    const urlSelection = useMemo(
        () => (previewOptions ? readPreviewSelection(previewOptions, searchParams) : null),
        [previewOptions, searchParams]
    );
    const defaultSelection = useMemo(
        () =>
            previewContextReady
                ? resolvePreviewDefaultSelection(previewOptions, savedGraphState)
                : null,
        [previewContextReady, previewOptions, savedGraphState]
    );
    const contextSelection = urlSelection ?? defaultSelection;
    const previewContext = useMemo(
        () =>
            previewOptions && previewContextData && contextSelection
                ? resolvePreviewContextFromQueryData(
                      previewOptions.entityType,
                      previewContextData,
                      contextSelection
                  )
                : null,
        [previewOptions, previewContextData, contextSelection]
    );
    const previewTimezone =
        previewContext &&
        (previewContext.kind === 'project'
            ? previewContext.project.timezone
            : previewContext.measuringPoint.timezone);
    const urlRange = useMemo(
        () => (previewTimezone ? readPreviewRange(searchParams, previewTimezone) : null),
        [searchParams, previewTimezone]
    );
    const previewSelection = previewContextReady ? urlSelection : null;
    const previewDateRange = previewContextReady ? urlRange : null;
    const activePreviewProjectId = previewSelection?.projectId ?? '';
    const activePreviewMeasuringPointId = previewSelection?.measuringPointId ?? '';

    useEffect(() => {
        if (
            !previewContextReady ||
            !previewContext ||
            !previewTimezone ||
            (urlSelection && urlRange)
        ) {
            return;
        }
        const selectionId =
            previewContext.kind === 'project'
                ? previewContext.project.id
                : previewContext.measuringPoint.id;
        setSearchParams(
            (current) => {
                const next = urlSelection
                    ? current
                    : writePreviewSelection(current, previewOptions.entityType, selectionId);
                return urlRange
                    ? next
                    : writePreviewRange(
                          next,
                          resolvePreviewDefaultRange(
                              savedGraphState,
                              initializedAt,
                              previewTimezone
                          ),
                          previewTimezone
                      );
            },
            { replace: true }
        );
    }, [
        previewContextReady,
        previewOptions,
        previewContext,
        previewTimezone,
        savedGraphState,
        initializedAt,
        urlSelection,
        urlRange,
        setSearchParams,
    ]);

    function onPreviewDateRangeChange(range: GraphTimeWindow) {
        if (!previewTimezone) throw new Error('Missing preview context timezone.');
        setSearchParams((current) => writePreviewRange(current, range, previewTimezone), {
            replace: true,
        });
    }

    const previewSelectorConfig = useMemo<PreviewSelectorConfig | null>(() => {
        if (entityType === ReportTemplateEntityType.Project) {
            return {
                description: gettext('PROJECT_TEMPLATE_PREVIEW_DESCRIPTION'),
                inputId: 'previewProjectId',
                label: gettext('PREVIEW_PROJECT'),
                value: activePreviewProjectId,
                options: previewProjectOptions,
            };
        }

        if (entityType === ReportTemplateEntityType.MeasuringPoint) {
            return {
                description: gettext('MEASURING_POINT_TEMPLATE_PREVIEW_DESCRIPTION'),
                inputId: 'previewMeasuringPointId',
                label: gettext('PREVIEW_MEASURING_POINT'),
                value: activePreviewMeasuringPointId,
                options: previewMeasuringPointOptions,
            };
        }

        if (!entityType) {
            return null;
        }

        throw createUnsupportedPreviewSelectionEntityTypeError(entityType);
    }, [
        activePreviewMeasuringPointId,
        activePreviewProjectId,
        entityType,
        previewMeasuringPointOptions,
        previewProjectOptions,
    ]);

    function assertPreviewOptionsAvailable() {
        if (entityType === ReportTemplateEntityType.Project && previewProjectOptions.length === 0) {
            throw new Error('No projects are available for project preview context.');
        }

        if (
            entityType === ReportTemplateEntityType.MeasuringPoint &&
            previewMeasuringPointOptions.length === 0
        ) {
            throw new Error(
                'No measuring points are available for measuring-point preview context.'
            );
        }

        if (
            entityType &&
            entityType !== ReportTemplateEntityType.Project &&
            entityType !== ReportTemplateEntityType.MeasuringPoint
        ) {
            throw createUnsupportedPreviewSelectionEntityTypeError(entityType);
        }
    }

    function onPreviewSelectionChange(event: ChangeEvent<HTMLSelectElement>) {
        if (!entityType) throw new Error('Missing report template entity type.');
        const nextId = event.target.value;
        setSearchParams((current) => writePreviewSelection(current, entityType, nextId), {
            replace: true,
        });
    }

    return {
        previewContextData,
        previewContext,
        previewContextLoading,
        previewContextError,
        previewSelection,
        previewDateRange,
        onPreviewDateRangeChange,
        previewSelectorConfig,
        assertPreviewOptionsAvailable,
        onPreviewSelectionChange,
    };
}
